import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Inbox, Loader2, X } from 'lucide-react';
import api from '../../utils/axios';
import { Button } from '../ui/button';
import { formatoMonto } from '../compartidos/useAnalisis';
import { fFechaHora } from './episodioUtils';

const Paginador = ({ pagina, paginas, onPagina }) => (paginas > 1 ? (
  <div className="flex items-center justify-end gap-2 pt-3 text-xs text-slate-500" data-testid="movs-caso-paginador">
    <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={pagina <= 1} onClick={() => onPagina(pagina - 1)} data-testid="movs-caso-anterior"><ChevronLeft size={14} /></Button>
    <span>Página {pagina} de {paginas}</span>
    <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={pagina >= paginas} onClick={() => onPagina(pagina + 1)} data-testid="movs-caso-siguiente"><ChevronRight size={14} /></Button>
  </div>
) : null);

// Movimientos del caso o de un episodio (episodio_movimientos), paginados de a 100, base actual e histórica.
// G5: un episodio sin movimientos lo dice en vez de mostrar todos los del caso.
export const MovimientosCasoTabla = ({ caseId, episodioId, onQuitar, renderExtra, version = 0 }) => {
  const [pagina, setPagina] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { setPagina(1); }, [episodioId]);
  useEffect(() => {
    let vivo = true;
    setData(null);
    setError(null);
    const url = episodioId ? `/cases/${caseId}/episodios/${episodioId}/movimientos` : `/cases/${caseId}/movimientos-caso`;
    api.get(url, { params: { pagina }, timeout: 180000 })
      .then((r) => vivo && setData(r.data))
      .catch((e) => vivo && setError(e.response?.data?.detail || 'No se pudieron cargar los movimientos'));
    return () => { vivo = false; };
  }, [caseId, episodioId, pagina, version]);

  if (error) return <p className="py-6 text-center text-sm text-red-500" data-testid="movs-caso-error">{error}</p>;
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" size={22} /></div>;
  if (!data.movimientos.length) {
    return (
      <div className="flex flex-col items-center py-10 text-slate-400" data-testid="movs-caso-vacio">
        <Inbox size={32} className="mb-2" />
        <p className="text-sm">{episodioId ? 'Este episodio no tiene movimientos cargados' : 'Este caso no tiene movimientos cargados'}</p>
      </div>
    );
  }
  return (
    <div data-testid="movs-caso-tabla">
      {renderExtra && renderExtra(data.movimientos)}
      <p className="mb-2 text-xs text-slate-500" data-testid="movs-caso-conteo">
        {data.encontrados} movimientos{data.total > data.encontrados ? ` (de ${data.total} referenciados; ${data.total - data.encontrados} no están en la base)` : ''}
        {data.avisos?.length ? ` · ${data.avisos.join(' ')}` : ''}
      </p>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Fecha</th><th className="px-3 py-2 font-medium">Sentido</th>
              <th className="px-3 py-2 text-right font-medium">Monto</th><th className="px-3 py-2 font-medium">Contraparte</th>
              <th className="px-3 py-2 font-medium">CVU propio</th><th className="px-3 py-2 font-medium">Sponsor</th>
              {onQuitar && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {data.movimientos.map((m) => (
              <tr key={m.id_waya} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`mov-caso-${m.id_waya}`}>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-slate-600">{fFechaHora(m.fecha)}</td>
                <td className="px-3 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.sentido === 'INGRESO' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{m.sentido}</span>
                </td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${m.sentido === 'INGRESO' ? 'text-emerald-700' : 'text-rose-700'}`}>{formatoMonto(m.monto)}</td>
                <td className="px-3 py-2">
                  <p className="max-w-[220px] truncate text-slate-800">{m.contraparte_nombre || '—'}</p>
                  <p className="font-mono text-[10px] text-slate-400">{m.contraparte_cuit || m.contraparte_cvu || ''}</p>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{m.cvu_propio || '—'}</td>
                <td className="px-3 py-2 text-slate-600">{m.sponsor}</td>
                {onQuitar && (
                  <td className="px-3 py-2 text-right">
                    {m.manual && (
                      <button type="button" onClick={() => onQuitar(m.id_waya)} title="Quitar movimiento vinculado a mano"
                        className="rounded p-1 text-red-500 hover:bg-red-50" data-testid={`mov-caso-quitar-${m.id_waya}`}><X size={13} /></button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginador pagina={data.pagina} paginas={data.paginas} onPagina={setPagina} />
    </div>
  );
};

export default MovimientosCasoTabla;
